import React, { useMemo, useState } from "react";
import axios from "axios";
import {
  Box, Button, Card, CircularProgress, Dialog, DialogContent,
  Divider, Fade, MenuItem, Select, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, TextField, Tooltip,
  Typography, alpha, Chip, IconButton,
} from "@mui/material";
import dayjs, { Dayjs } from "dayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import {
  useGetBalanceSheetAdvancedQuery,
  useGetBalanceSheetSummaryQuery,
  useGetBalanceSheetIncomeQuery,
  useGetBalanceSheetExpensesQuery,
  useGetBalanceSheetTransactionsQuery,
  useGetTripProfitQuery,
  useGetTripProfitMonthlyQuery,
} from "../../../Api/attoDeskApi";
import {
  IBalanceSheetIncome, IBalanceSheetExpense,
  IBalanceSheetTransaction, IBalanceSheetSummary, IBalanceSheetAdvanced,
  ITripProfit, ITripProfitMonthly, ITripProfitSummary,
} from "../../../Api/Interface/api.interface";

// Icons
import TrendingUpIcon             from "@mui/icons-material/TrendingUp";
import TrendingDownIcon           from "@mui/icons-material/TrendingDown";
import AccountBalanceIcon         from "@mui/icons-material/AccountBalance";
import FilterListIcon             from "@mui/icons-material/FilterList";
import FileDownloadOutlinedIcon   from "@mui/icons-material/FileDownloadOutlined";
import TableChartOutlinedIcon     from "@mui/icons-material/TableChartOutlined";
import * as XLSX from "xlsx";
import PictureAsPdfOutlinedIcon   from "@mui/icons-material/PictureAsPdfOutlined";
import ReceiptLongOutlinedIcon    from "@mui/icons-material/ReceiptLongOutlined";
import CategoryOutlinedIcon       from "@mui/icons-material/CategoryOutlined";
import CalendarMonthOutlinedIcon  from "@mui/icons-material/CalendarMonthOutlined";
import CloseIcon                  from "@mui/icons-material/Close";
import SearchIcon                 from "@mui/icons-material/Search";
import LightbulbOutlinedIcon      from "@mui/icons-material/LightbulbOutlined";
import EmojiEventsOutlinedIcon    from "@mui/icons-material/EmojiEventsOutlined";
import ShowChartIcon              from "@mui/icons-material/ShowChart";
import PersonOutlineIcon          from "@mui/icons-material/PersonOutline";

// ── Constants ──────────────────────────────────────────────────────────────────
const BRAND  = "#2f3891";
const BORDER = "#e2e8f0";

const METHOD_CFG: Record<string, { label: string; color: string; bg: string; border: string; activeBorder: string; glow: string; icon: string }> = {
  cash:        { label: "Cash",           color: "#16a34a", bg: "#f0fdf4", border: "#bbf7d0", activeBorder: "#16a34a", glow: "rgba(22,163,74,0.15)",  icon: "💵" },
  etransfer:   { label: "E-Transfer",     color: "#2f3891", bg: "#eef2ff", border: "#c7d2fe", activeBorder: "#2f3891", glow: "rgba(99,102,241,0.15)", icon: "🔄" },
  credit_card: { label: "Credit Card",    color: "#0ea5e9", bg: "#f0f9ff", border: "#bae6fd", activeBorder: "#0ea5e9", glow: "rgba(14,165,233,0.15)", icon: "💳" },
  debit_card:  { label: "Debit Card",     color: "#2f3891", bg: "#f5f3ff", border: "#ddd6fe", activeBorder: "#2f3891", glow: "rgba(139,92,246,0.15)", icon: "🏧" },
  cheque:      { label: "Cheque",         color: "#f59e0b", bg: "#fffbeb", border: "#fde68a", activeBorder: "#f59e0b", glow: "rgba(245,158,11,0.15)", icon: "📝" },
  po:          { label: "Purchase Order", color: "#d97706", bg: "#fffbeb", border: "#fde68a", activeBorder: "#d97706", glow: "rgba(217,119,6,0.15)",  icon: "📋" },
};
const ALL_METHODS = ["cash", "etransfer", "credit_card", "debit_card", "cheque", "po"];

const thSx = {
  fontWeight: 700, fontSize: 11, color: "#64748b", textTransform: "uppercase" as const,
  letterSpacing: "0.06em", py: 1.25, px: 1.5,
  borderBottom: `1px solid ${BORDER}`, bgcolor: "#f8fafc", whiteSpace: "nowrap" as const,
};
const tdSx = {
  py: 1.25, px: 1.5, borderBottom: "1px solid #f1f5f9",
  fontSize: 12, color: "#1e293b", verticalAlign: "middle" as const,
};

const fmt = (n: number) =>
  new Intl.NumberFormat("en-CA", { style: "currency", currency: "CAD", maximumFractionDigits: 2 }).format(n);

// ── MethodChip ─────────────────────────────────────────────────────────────────
const MethodChip: React.FC<{ method: string | null }> = ({ method }) => {
  if (!method) return <Typography sx={{ fontSize: 11, color: "#cbd5e1" }}>—</Typography>;
  const cfg = METHOD_CFG[method] ?? { label: method, color: "#64748b", bg: "#f8fafc", border: "#e2e8f0", icon: "" };
  return (
    <Box component="span" sx={{
      display: "inline-flex", alignItems: "center", gap: 0.5,
      px: 1.25, py: 0.3, borderRadius: 1.5,
      fontSize: 11, fontWeight: 700, color: cfg.color, bgcolor: cfg.bg, border: `1px solid ${cfg.border}`,
    }}>
      {cfg.icon && <span style={{ fontSize: 10 }}>{cfg.icon}</span>}
      {cfg.label}
    </Box>
  );
};

// ── Transaction Detail Modal ──────────────────────────────────────────────────
const TransactionModal: React.FC<{ row: IBalanceSheetTransaction | null; onClose: () => void }> = ({ row, onClose }) => {
  if (!row) return null;
  const isIncome = row.type === "income";
  const cfg = row.method ? (METHOD_CFG[row.method] ?? { label: row.method, color: "#64748b", bg: "#f8fafc", border: "#e2e8f0", icon: "" }) : null;
  const accentColor = isIncome ? "#16a34a" : "#ef4444";
  const accentBg    = isIncome ? "#f0fdf4" : "#fef2f2";
  const gradient    = isIncome ? "linear-gradient(135deg, #16a34a 0%, #22d3ee 100%)" : "linear-gradient(135deg, #ef4444 0%, #f97316 100%)";

  return (
    <Dialog open={!!row} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3, overflow: "visible" } }}>
      <Box sx={{ position: "relative" }}>
        {/* Header */}
        <Box sx={{
          background: gradient,
          px: 3, py: 2.5, borderRadius: "12px 12px 0 0",
          display: "flex", alignItems: "flex-start", justifyContent: "space-between",
        }}>
          <Box>
            <Typography sx={{ fontSize: 11, fontWeight: 700, color: "rgba(255,255,255,0.7)", letterSpacing: "0.08em", textTransform: "uppercase" }}>
              Transaction Details
            </Typography>
            <Typography sx={{ fontSize: 22, fontWeight: 800, color: "#fff", lineHeight: 1.2, mt: 0.25 }}>
              {row.reference || `#${row.id}`}
            </Typography>
          </Box>
          <IconButton onClick={onClose} sx={{ color: "rgba(255,255,255,0.8)", "&:hover": { bgcolor: "rgba(255,255,255,0.15)" } }}>
            <CloseIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Box>

        <DialogContent sx={{ p: 3 }}>
          {/* Amount hero */}
          <Box sx={{
            textAlign: "center", py: 2.5, mb: 2.5,
            bgcolor: accentBg, borderRadius: 2.5, border: `1px solid ${BORDER}`,
          }}>
            <Typography sx={{ fontSize: 11, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.07em", fontWeight: 600 }}>
              {isIncome ? "Amount Received" : "Amount Spent"}
            </Typography>
            <Typography sx={{ fontSize: 36, fontWeight: 900, color: accentColor, lineHeight: 1.1, mt: 0.5 }}>
              {isIncome ? "+" : "−"}{fmt(row.amount)}
            </Typography>
            {row.date && (
              <Typography sx={{ fontSize: 12, color: "#64748b", mt: 0.5 }}>
                {dayjs(row.date).format("dddd, MMMM D, YYYY")}
              </Typography>
            )}
          </Box>

          {/* Detail rows */}
          {[
            { label: "Type",            value: isIncome ? "Income" : "Expense" },
            { label: "Reference",       value: row.reference || "—" },
            { label: "Description",     value: row.description || "—" },
            { label: "Running Balance", value: fmt(row.balance_after) },
          ].map(({ label, value }) => (
            <Box key={label} sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", py: 1.25, borderBottom: "1px solid #f1f5f9" }}>
              <Typography sx={{ fontSize: 11, fontWeight: 600, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.06em", flexShrink: 0, mr: 2 }}>
                {label}
              </Typography>
              <Typography sx={{ fontSize: 13, fontWeight: 600, color: "#0f172a", textAlign: "right" }}>{value}</Typography>
            </Box>
          ))}

          {/* Payment method badge */}
          {cfg && (
            <Box sx={{ mt: 2, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <Typography sx={{ fontSize: 11, fontWeight: 600, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.06em" }}>
                Payment Method
              </Typography>
              <Box sx={{
                display: "inline-flex", alignItems: "center", gap: 0.75,
                px: 1.75, py: 0.6, borderRadius: 2,
                fontSize: 13, fontWeight: 700, color: cfg.color, bgcolor: cfg.bg, border: `1.5px solid ${cfg.border}`,
              }}>
                {cfg.icon} {cfg.label}
              </Box>
            </Box>
          )}

          {/* Type badge */}
          <Box sx={{ mt: 2, display: "flex", justifyContent: "center" }}>
            <Box sx={{
              px: 2, py: 0.6, borderRadius: 2,
              fontSize: 12, fontWeight: 700,
              color: accentColor, bgcolor: accentBg,
              border: `1.5px solid ${accentColor}30`,
            }}>
              {isIncome ? "💰 Income Transaction" : "📤 Expense Transaction"}
            </Box>
          </Box>
        </DialogContent>
      </Box>
    </Dialog>
  );
};

// ── SectionTitle ───────────────────────────────────────────────────────────────
const SectionTitle: React.FC<{ title: string; subtitle?: string }> = ({ title, subtitle }) => (
  <Box sx={{ mb: 1.5 }}>
    <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#0f172a" }}>{title}</Typography>
    {subtitle && <Typography sx={{ fontSize: 11, color: "#94a3b8", mt: 0.25 }}>{subtitle}</Typography>}
  </Box>
);

// ── Export helpers ─────────────────────────────────────────────────────────────
const exportCSV = (rows: any[], filename: string, headers: string[], cols: string[]) => {
  const csv = [headers, ...rows.map((r) => cols.map((c) => `"${String(r[c] ?? "").replace(/"/g, '""')}"`))].map((r) => r.join(",")).join("\n");
  const a = document.createElement("a");
  a.href = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
  a.download = filename; a.click();
};

const exportPDF = (title: string, headers: string[], rows: string[][], summary: { income: number; expense: number; net: number }, filterNote?: string) => {
  const tableRows = rows.map((r) => `<tr>${r.map((c) => `<td>${c}</td>`).join("")}</tr>`).join("");
  const html = `<!DOCTYPE html><html><head><title>${title}</title>
  <style>
    body{font-family:Arial,sans-serif;font-size:11px;color:#1e293b;margin:20px}
    h2{font-size:16px;margin-bottom:2px}p{font-size:11px;color:#64748b;margin:0 0 12px}
    .filter-note{background:#f8fafc;border:1px solid #e2e8f0;border-radius:4px;padding:6px 10px;font-size:10px;color:#2f3891;margin-bottom:12px;display:inline-block}
    .s{display:flex;gap:24px;margin-bottom:16px}.sc{border:1px solid #e2e8f0;border-radius:6px;padding:10px 16px;min-width:140px}
    .sl{font-size:10px;color:#64748b;text-transform:uppercase;letter-spacing:.05em}.sv{font-size:18px;font-weight:bold}
    table{width:100%;border-collapse:collapse}
    th{background:#f8fafc;font-size:10px;text-transform:uppercase;letter-spacing:.05em;color:#64748b;padding:7px 8px;border:1px solid #e2e8f0;text-align:left}
    td{padding:6px 8px;border:1px solid #e2e8f0}tr:nth-child(even) td{background:#f8fafc}
  </style></head><body>
  <h2>${title}</h2><p>Generated ${dayjs().format("DD MMM YYYY, h:mm A")}</p>
  ${filterNote ? `<div class="filter-note">🔍 ${filterNote}</div>` : ""}
  <div class="s">
    <div class="sc"><div class="sl">Total Income</div><div class="sv" style="color:#16a34a">${fmt(summary.income)}</div></div>
    <div class="sc"><div class="sl">Total Expenses</div><div class="sv" style="color:#ef4444">${fmt(summary.expense)}</div></div>
    <div class="sc"><div class="sl">Net Balance</div><div class="sv" style="color:${summary.net >= 0 ? "#2f3891" : "#ef4444"}">${fmt(summary.net)}</div></div>
  </div>
  <table><thead><tr>${headers.map((h) => `<th>${h}</th>`).join("")}</tr></thead>
  <tbody>${tableRows}</tbody></table>
  <script>window.onload=()=>{window.print();}<\/script></body></html>`;
  const win = window.open("", "_blank");
  if (win) { win.document.write(html); win.document.close(); }
};

// ── Invoice Detail Modal ───────────────────────────────────────────────────────
const InvoiceModal: React.FC<{ row: IBalanceSheetIncome | null; onClose: () => void }> = ({ row, onClose }) => {
  if (!row) return null;
  const cfg = row.payment_method ? (METHOD_CFG[row.payment_method] ?? { label: row.payment_method, color: "#64748b", bg: "#f8fafc", border: "#e2e8f0", icon: "" }) : null;
  return (
    <Dialog open={!!row} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3, overflow: "visible" } }}>
      <Box sx={{ position: "relative" }}>
        {/* Header */}
        <Box sx={{
          background: `linear-gradient(135deg, ${BRAND} 0%, #818cf8 100%)`,
          px: 3, py: 2.5, borderRadius: "12px 12px 0 0",
          display: "flex", alignItems: "flex-start", justifyContent: "space-between",
        }}>
          <Box>
            <Typography sx={{ fontSize: 11, fontWeight: 700, color: "rgba(255,255,255,0.7)", letterSpacing: "0.08em", textTransform: "uppercase" }}>
              Invoice Details
            </Typography>
            <Typography sx={{ fontSize: 22, fontWeight: 800, color: "#fff", lineHeight: 1.2, mt: 0.25 }}>
              {row.InvoiceNo ? `#${row.InvoiceNo}` : `Payment #${row.id}`}
            </Typography>
          </Box>
          <IconButton onClick={onClose} sx={{ color: "rgba(255,255,255,0.8)", "&:hover": { bgcolor: "rgba(255,255,255,0.15)" } }}>
            <CloseIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Box>

        <DialogContent sx={{ p: 3 }}>
          {/* Amount hero */}
          <Box sx={{
            textAlign: "center", py: 2.5, mb: 2.5,
            bgcolor: "#f8fafc", borderRadius: 2.5, border: `1px solid ${BORDER}`,
          }}>
            <Typography sx={{ fontSize: 11, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.07em", fontWeight: 600 }}>
              Amount Received
            </Typography>
            <Typography sx={{ fontSize: 36, fontWeight: 900, color: "#16a34a", lineHeight: 1.1, mt: 0.5 }}>
              {fmt(row.amount)}
            </Typography>
            {row.payment_date && (
              <Typography sx={{ fontSize: 12, color: "#64748b", mt: 0.5 }}>
                {dayjs(row.payment_date).format("dddd, MMMM D, YYYY")}
              </Typography>
            )}
          </Box>

          {/* Detail rows */}
          {[
            { label: "Invoice No",   value: row.InvoiceNo || "—" },
            { label: "Work Order",   value: row.quoteWorkOrderNo || "—" },
            { label: "Client",       value: row.client_name?.trim() || "—" },
            { label: "Description",  value: row.description || "—" },
          ].map(({ label, value }) => (
            <Box key={label} sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", py: 1.25, borderBottom: "1px solid #f1f5f9" }}>
              <Typography sx={{ fontSize: 11, fontWeight: 600, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.06em", flexShrink: 0, mr: 2 }}>
                {label}
              </Typography>
              <Typography sx={{ fontSize: 13, fontWeight: 600, color: "#0f172a", textAlign: "right" }}>{value}</Typography>
            </Box>
          ))}

          {/* Payment method badge */}
          {cfg && (
            <Box sx={{ mt: 2, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <Typography sx={{ fontSize: 11, fontWeight: 600, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.06em" }}>
                Payment Method
              </Typography>
              <Box sx={{
                display: "inline-flex", alignItems: "center", gap: 0.75,
                px: 1.75, py: 0.6, borderRadius: 2,
                fontSize: 13, fontWeight: 700, color: cfg.color, bgcolor: cfg.bg, border: `1.5px solid ${cfg.border}`,
              }}>
                {cfg.icon} {cfg.label}
              </Box>
            </Box>
          )}
        </DialogContent>
      </Box>
    </Dialog>
  );
};

// ── Main Component ─────────────────────────────────────────────────────────────
const BalanceSheet: React.FC = () => {
  const [dateFrom, setDateFrom]           = useState("");
  const [dateTo, setDateTo]               = useState("");
  const [paymentMethod, setPaymentMethod] = useState("");
  const [activeTab, setActiveTab]         = useState<"income" | "expenses" | "transactions" | "profit">("income");

  // Active card filter (client-side, independent of API filter)
  const [activeMethodCard, setActiveMethodCard] = useState<string | null>(null);
  // Client-side search
  const [clientSearch, setClientSearch] = useState("");
  // Invoice detail modal
  const [selectedInvoice, setSelectedInvoice] = useState<IBalanceSheetIncome | null>(null);
  const [selectedTx, setSelectedTx] = useState<IBalanceSheetTransaction | null>(null);
  const [pdfLoading, setPdfLoading] = useState(false);
  const API_URL = process.env.REACT_APP_API_URL;

  const handleDownloadPdf = async () => {
    setPdfLoading(true);
    try {
      const params: Record<string, string> = {};
      if (dateFrom)       params.date_from       = dateFrom;
      if (dateTo)         params.date_to         = dateTo;
      if (paymentMethod)  params.payment_method  = paymentMethod;
      const response = await axios.get(`${API_URL}/pdf/balance-sheet-report`, {
        params, responseType: "blob",
        headers: { Authorization: `Bearer ${localStorage.getItem("token") || ""}` },
      });
      const url  = window.URL.createObjectURL(new Blob([response.data], { type: "application/pdf" }));
      window.open(url, "_blank");
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `balance-sheet-${new Date().toISOString().slice(0, 10)}.pdf`);
      document.body.appendChild(link); link.click(); link.remove();
      setTimeout(() => window.URL.revokeObjectURL(url), 10000);
    } catch { /* silent */ } finally { setPdfLoading(false); }
  };

  // ── API filters (drive all server calls) ────────────────────────────────────
  const filters = useMemo(() => ({
    date_from:      dateFrom      || undefined,
    date_to:        dateTo        || undefined,
    payment_method: paymentMethod || undefined,
  }), [dateFrom, dateTo, paymentMethod]);

  const { data: advancedData, isLoading: advancedLoading } = useGetBalanceSheetAdvancedQuery(filters);
  const { data: summaryData,  isLoading: summaryLoading  } = useGetBalanceSheetSummaryQuery(filters);
  const { data: incomeData,   isLoading: incomeLoading   } = useGetBalanceSheetIncomeQuery(filters);
  const { data: expensesData, isLoading: expensesLoading } = useGetBalanceSheetExpensesQuery(filters);
  const { data: txData,       isLoading: txLoading       } = useGetBalanceSheetTransactionsQuery(filters);

  const profitFilter = useMemo(() => ({
    date_from: dateFrom || undefined,
    date_to:   dateTo   || undefined,
  }), [dateFrom, dateTo]);
  const { data: tripProfitListData, isLoading: profitLoading } = useGetTripProfitQuery(profitFilter);
  const { data: tripProfitMonthlyData }                        = useGetTripProfitMonthlyQuery(profitFilter);

  // ── Derived data ─────────────────────────────────────────────────────────────
  const summary: IBalanceSheetSummary = useMemo(() =>
    (summaryData as any)?.data ?? { total_income: 0, total_expense: 0, net_balance: 0, breakdown: {} },
  [summaryData]);

  const advanced: IBalanceSheetAdvanced = useMemo(() =>
    (advancedData as any)?.data ?? { monthly: [], tax: { collected: 0, paid: 0, net: 0 }, invoice_status: { paid: 0, outstanding: 0, partial: 0, paid_count: 0, outstanding_count: 0, partial_count: 0 }, insights: { this_month_profit: 0, top_expense_category: "—", top_expense_amount: 0, top_client_name: "—", top_client_amount: 0 } },
  [advancedData]);

  const allIncomeRows: IBalanceSheetIncome[] = useMemo(() => (incomeData   as any)?.data ?? [], [incomeData]);
  const expenseRows: IBalanceSheetExpense[]  = useMemo(() => (expensesData as any)?.data ?? [], [expensesData]);
  const txRows: IBalanceSheetTransaction[]   = useMemo(() => (txData as any)?.data ?? [], [txData]);

  const tripProfitRows: ITripProfit[]         = useMemo(() => (tripProfitListData as any)?.data    ?? [], [tripProfitListData]);
  const tripProfitSummary: ITripProfitSummary = useMemo(() => (tripProfitListData as any)?.summary ?? { total_revenue: 0, total_driver_cost: 0, total_expenses: 0, total_profit: 0, profit_margin: 0 }, [tripProfitListData]);
  const tripProfitMonthly: ITripProfitMonthly[] = useMemo(() => (tripProfitMonthlyData as any)?.data ?? [], [tripProfitMonthlyData]);

  // ── Driver profit aggregation (grouped from trip-level data) ────────────────
  interface DriverProfitRow { driver: string; trips: number; revenue: number; driverCost: number; expenses: number; profit: number; margin: number; }
  const driverProfitRows: DriverProfitRow[] = useMemo(() => {
    const map: Record<string, { trips: number; revenue: number; driverCost: number; expenses: number }> = {};
    for (const row of tripProfitRows) {
      const names = row.driver_names ? row.driver_names.split(",").map(n => n.trim()).filter(Boolean) : ["Unassigned"];
      for (const name of names) {
        if (!map[name]) map[name] = { trips: 0, revenue: 0, driverCost: 0, expenses: 0 };
        map[name].trips    += 1;
        map[name].revenue  += row.revenue;
        map[name].driverCost += row.driver_cost;
        map[name].expenses += row.trip_expenses;
      }
    }
    return Object.entries(map).map(([driver, d]) => {
      const profit = d.revenue - d.driverCost - d.expenses;
      return { driver, ...d, profit, margin: d.revenue > 0 ? Math.round((profit / d.revenue) * 100 * 10) / 10 : 0 };
    }).sort((a, b) => b.revenue - a.revenue);
  }, [tripProfitRows]);

  // ── Client-side filtered income rows ─────────────────────────────────────────
  const incomeRows: IBalanceSheetIncome[] = useMemo(() => {
    let rows = allIncomeRows;
    if (activeMethodCard) rows = rows.filter(r => r.payment_method === activeMethodCard);
    if (clientSearch.trim()) {
      const q = clientSearch.trim().toLowerCase();
      rows = rows.filter(r =>
        (r.client_name ?? "").toLowerCase().includes(q) ||
        (r.InvoiceNo ?? "").toLowerCase().includes(q) ||
        (r.quoteWorkOrderNo ?? "").toLowerCase().includes(q) ||
        (r.description ?? "").toLowerCase().includes(q)
      );
    }
    return rows;
  }, [allIncomeRows, activeMethodCard, clientSearch]);

  // ── Per-method stats for cards ────────────────────────────────────────────────
  const methodStats = useMemo(() => {
    const stats: Record<string, { income: number; count: number }> = {};
    for (const m of ALL_METHODS) {
      const rows = allIncomeRows.filter(r => r.payment_method === m);
      stats[m] = { income: rows.reduce((s, r) => s + (+r.amount || 0), 0), count: rows.length };
    }
    return stats;
  }, [allIncomeRows]);

  // ── Smart insights ────────────────────────────────────────────────────────────
  const smartInsights = useMemo(() => {
    const withData = ALL_METHODS.filter(m => methodStats[m].count > 0);
    if (withData.length === 0) return null;
    const mostUsed     = withData.sort((a, b) => methodStats[b].count   - methodStats[a].count)[0];
    const highestEarn  = withData.sort((a, b) => methodStats[b].income  - methodStats[a].income)[0];
    const lowestEarn   = withData.length > 1 ? withData.sort((a, b) => methodStats[a].income  - methodStats[b].income)[0] : null;
    return { mostUsed, highestEarn, lowestEarn };
  }, [methodStats]);

  // ── Effective summary values (account for client-side method card filter) ────
  const effectiveSummary = useMemo(() => {
    if (!activeMethodCard) return summary;
    // When a method card is clicked, recalculate income from filtered rows
    const filteredIncome = allIncomeRows
      .filter(r => r.payment_method === activeMethodCard)
      .reduce((s, r) => s + (+r.amount || 0), 0);
    return {
      ...summary,
      total_income: filteredIncome,
      net_balance:  filteredIncome - summary.total_expense,
    };
  }, [summary, allIncomeRows, activeMethodCard]);

  const hasFilters = dateFrom || dateTo || paymentMethod || activeMethodCard || clientSearch;

  const handleCardClick = (method: string) => {
    setActiveMethodCard(prev => prev === method ? null : method);
    if (activeTab !== "income") setActiveTab("income");
  };

  const clearAllFilters = () => {
    setDateFrom(""); setDateTo(""); setPaymentMethod("");
    setActiveMethodCard(null); setClientSearch("");
  };

  // ── Highcharts: Monthly Profit Chart ─────────────────────────────────────────
  const monthlyChartOpts: Highcharts.Options = useMemo(() => ({
    chart: { type: "column", height: 280, backgroundColor: "transparent", spacing: [10, 10, 20, 10] },
    title: { text: undefined }, credits: { enabled: false },
    legend: { enabled: true, itemStyle: { fontSize: "11px", fontWeight: "600", color: "#374151" } },
    xAxis: { categories: advanced.monthly.map(m => m.month), labels: { style: { fontSize: "11px", color: "#64748b" } }, lineColor: BORDER, tickColor: BORDER },
    yAxis: { title: { text: undefined }, labels: { formatter: function(this: any) { return `$${Number(this.value).toLocaleString()}`; }, style: { fontSize: "11px", color: "#64748b" } }, gridLineColor: "#f1f5f9" },
    tooltip: { shared: true, formatter: function(this: any) { return `<b>${this.x}</b><br>` + (this.points || []).map((p: any) => `<span style="color:${p.color}">●</span> ${p.series.name}: <b>${fmt(p.y)}</b>`).join("<br>"); } },
    plotOptions: { column: { borderRadius: 4, groupPadding: 0.1 }, spline: { lineWidth: 2.5, marker: { radius: 4 } } },
    series: [
      { type: "column", name: "Income",   data: advanced.monthly.map(m => m.income),   color: "#16a34a" },
      { type: "column", name: "Expenses", data: advanced.monthly.map(m => m.expenses), color: "#ef4444" },
      { type: "spline", name: "Profit",   data: advanced.monthly.map(m => m.profit),   color: BRAND, zIndex: 5, lineWidth: 3, marker: { radius: 5, fillColor: "#fff", lineWidth: 2, lineColor: BRAND } },
    ],
  }), [advanced.monthly]);

  // ── Highcharts: Invoice Status Donut ─────────────────────────────────────────
  const invoiceDonutOpts: Highcharts.Options = useMemo(() => {
    const { paid, outstanding, partial, paid_count, outstanding_count, partial_count } = advanced.invoice_status;
    return {
      chart: { type: "pie", height: 220, backgroundColor: "transparent" }, title: { text: undefined }, credits: { enabled: false },
      tooltip: { pointFormatter: function(this: any) { return `<b>${fmt(this.y)}</b> (${this.custom?.count} invoice${this.custom?.count !== 1 ? "s" : ""})`; } },
      plotOptions: { pie: { innerSize: "60%", dataLabels: { enabled: false }, showInLegend: true } },
      legend: { enabled: true, layout: "vertical", align: "right", verticalAlign: "middle", itemStyle: { fontSize: "11px", fontWeight: "600", color: "#374151" } },
      series: [{ type: "pie", name: "Invoices", data: [
        { name: `Paid (${paid_count})`,                  y: paid,        color: "#16a34a", custom: { count: paid_count } },
        { name: `Outstanding (${outstanding_count})`,    y: outstanding, color: "#ef4444", custom: { count: outstanding_count } },
        { name: `Partial (${partial_count})`,            y: partial,     color: "#f59e0b", custom: { count: partial_count } },
      ].filter(d => d.y > 0) }],
    };
  }, [advanced.invoice_status]);

  // ── Highcharts: Trip Profit Monthly ──────────────────────────────────────────
  const tripProfitChartOpts: Highcharts.Options = useMemo(() => ({
    chart: { type: "column", height: 260, backgroundColor: "transparent", spacing: [10, 10, 20, 10] },
    title: { text: undefined }, credits: { enabled: false },
    legend: { enabled: true, itemStyle: { fontSize: "11px", fontWeight: "600", color: "#374151" } },
    xAxis: { categories: tripProfitMonthly.map(m => m.month), labels: { style: { fontSize: "11px", color: "#64748b" } }, lineColor: BORDER, tickColor: BORDER },
    yAxis: { title: { text: undefined }, labels: { formatter: function(this: any) { return `$${Number(this.value).toLocaleString()}`; }, style: { fontSize: "11px", color: "#64748b" } }, gridLineColor: "#f1f5f9" },
    tooltip: { shared: true, formatter: function(this: any) { return `<b>${this.x}</b><br>` + (this.points || []).map((p: any) => `<span style="color:${p.color}">●</span> ${p.series.name}: <b>${fmt(p.y)}</b>`).join("<br>"); } },
    plotOptions: { column: { borderRadius: 4, groupPadding: 0.1 }, spline: { lineWidth: 2.5, marker: { radius: 4 } } },
    series: [
      { type: "column", name: "Revenue",     data: tripProfitMonthly.map(m => m.revenue),    color: "#2f3891" },
      { type: "column", name: "Driver Cost", data: tripProfitMonthly.map(m => m.driver_cost), color: "#f59e0b" },
      { type: "spline", name: "Profit",      data: tripProfitMonthly.map(m => m.profit),      color: "#16a34a", zIndex: 5, lineWidth: 3, marker: { radius: 5, fillColor: "#fff", lineWidth: 2, lineColor: "#16a34a" } },
    ],
  }), [tripProfitMonthly]);

  // ── Filter note text (for exports / drill-down header) ──────────────────────
  const filterNote = useMemo(() => {
    const parts: string[] = [];
    if (activeMethodCard) parts.push(METHOD_CFG[activeMethodCard]?.label ?? activeMethodCard);
    if (paymentMethod && !activeMethodCard) parts.push(METHOD_CFG[paymentMethod]?.label ?? paymentMethod);
    if (dateFrom) parts.push(`From ${dayjs(dateFrom).format("MMM D, YYYY")}`);
    if (dateTo)   parts.push(`To ${dayjs(dateTo).format("MMM D, YYYY")}`);
    if (clientSearch) parts.push(`"${clientSearch}"`);
    return parts.join(" · ");
  }, [activeMethodCard, paymentMethod, dateFrom, dateTo, clientSearch]);

  // ── Export handlers (respect active filters) ─────────────────────────────────
  const handleExportIncomeCSV = () => exportCSV(
    incomeRows, `income_${dayjs().format("YYYY-MM-DD")}.csv`,
    ["Invoice No", "Work Order", "Client", "Date", "Amount", "Method", "Description"],
    ["InvoiceNo", "quoteWorkOrderNo", "client_name", "payment_date", "amount", "payment_method", "description"],
  );
  const handleExportExpensesCSV = () => exportCSV(
    expenseRows, `expenses_${dayjs().format("YYYY-MM-DD")}.csv`,
    ["ID", "Category", "Description", "Date", "Amount", "HST", "Total", "Status"],
    ["id", "category", "description", "date", "amount", "hst", "total", "status"],
  );

  const handleExportIncomeExcel = () => {
    const data = incomeRows.map((r, i) => ({
      "#":           i + 1,
      "Invoice No":  r.InvoiceNo || "",
      "Work Order":  r.quoteWorkOrderNo || "",
      "Client":      r.client_name || "",
      "Date":        r.payment_date ? dayjs(r.payment_date).format("DD MMM YYYY") : "",
      "Amount":      Number(r.amount || 0).toFixed(2),
      "Method":      r.payment_method || "",
      "Description": r.description || "",
    }));
    const ws = XLSX.utils.json_to_sheet(data);
    ws["!cols"] = Object.keys(data[0] || {}).map((k) => ({ wch: Math.max(k.length, ...data.map((r: any) => String(r[k] || "").length)) + 2 }));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Income");
    XLSX.writeFile(wb, `income_${dayjs().format("YYYYMMDD")}.xlsx`);
  };

  const handleExportExpensesExcel = () => {
    const data = expenseRows.map((r, i) => ({
      "#":           i + 1,
      "Category":    r.category || "",
      "Description": r.description || "",
      "Date":        r.date ? dayjs(r.date).format("DD MMM YYYY") : "",
      "Amount":      Number(r.amount || 0).toFixed(2),
      "HST":         Number(r.hst || 0).toFixed(2),
      "Total":       Number(r.total || 0).toFixed(2),
      "Status":      r.status || "",
    }));
    const ws = XLSX.utils.json_to_sheet(data);
    ws["!cols"] = Object.keys(data[0] || {}).map((k) => ({ wch: Math.max(k.length, ...data.map((r: any) => String(r[k] || "").length)) + 2 }));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Expenses");
    XLSX.writeFile(wb, `expenses_${dayjs().format("YYYYMMDD")}.xlsx`);
  };
  const handleExportIncomePDF = () => exportPDF(
    "Balance Sheet — Income",
    ["Invoice No", "Work Order", "Client", "Date", "Amount", "Method"],
    incomeRows.map(r => [r.InvoiceNo || "—", r.quoteWorkOrderNo || "—", r.client_name || "—", r.payment_date ? dayjs(r.payment_date).format("DD MMM YYYY") : "—", fmt(r.amount), r.payment_method || "—"]),
    { income: effectiveSummary.total_income, expense: effectiveSummary.total_expense, net: effectiveSummary.net_balance },
    filterNote || undefined,
  );
  const handleExportExpensesPDF = () => exportPDF(
    "Balance Sheet — Expenses",
    ["ID", "Category", "Description", "Date", "Amount", "Total", "Status"],
    expenseRows.map(r => [`EXP-${r.id}`, r.category || "—", r.description, r.date ? dayjs(r.date).format("DD MMM YYYY") : "—", fmt(r.amount), fmt(r.total ?? r.amount), r.status || "—"]),
    { income: effectiveSummary.total_income, expense: effectiveSummary.total_expense, net: effectiveSummary.net_balance },
  );
  const handleExportTransactionsExcel = () => {
    const data = txRows.map((r, i) => ({
      "#":               i + 1,
      "Date":            r.date ? dayjs(r.date).format("DD MMM YYYY") : "",
      "Type":            r.type === "income" ? "Income" : "Expense",
      "Reference":       r.reference || "",
      "Method":          r.method || "",
      "Amount":          (r.type === "income" ? "+" : "-") + Number(r.amount || 0).toFixed(2),
      "Running Balance": Number(r.balance_after || 0).toFixed(2),
    }));
    const ws = XLSX.utils.json_to_sheet(data);
    ws["!cols"] = Object.keys(data[0] || {}).map((k) => ({ wch: Math.max(k.length, ...data.map((r: any) => String(r[k] || "").length)) + 2 }));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Transactions");
    XLSX.writeFile(wb, `transactions_${dayjs().format("YYYYMMDD")}.xlsx`);
  };

  const handleExportTxPDF = () => exportPDF(
    "Balance Sheet — Transactions",
    ["Date", "Type", "Reference", "Method", "Amount", "Balance"],
    txRows.map(r => [r.date ? dayjs(r.date).format("DD MMM YYYY") : "—", r.type === "income" ? "Income" : "Expense", r.reference || "—", r.method || "—", `${r.type === "income" ? "+" : "-"}${fmt(r.amount)}`, fmt(r.balance_after)]),
    { income: effectiveSummary.total_income, expense: effectiveSummary.total_expense, net: effectiveSummary.net_balance },
  );

  return (
    <Box sx={{ p: 3, bgcolor: "#f8fafc", minHeight: "100vh" }}>
      {/* ── Invoice Detail Modal ── */}
      <InvoiceModal row={selectedInvoice} onClose={() => setSelectedInvoice(null)} />
      <TransactionModal row={selectedTx} onClose={() => setSelectedTx(null)} />

      {/* ── Page Header ── */}
      <Box sx={{ mb: 3, display: "flex", alignItems: "flex-start", justifyContent: "space-between", flexWrap: "wrap", gap: 1 }}>
        <Box>
          <Typography sx={{ fontSize: 24, fontWeight: 800, color: "#0f172a" }}>Balance Sheet</Typography>
          <Typography sx={{ fontSize: 13, color: "#64748b", mt: 0.5 }}>
            Overall income, expenses, payment breakdown, and net balance
          </Typography>
        </Box>
        {hasFilters && (
          <Button size="small" onClick={clearAllFilters}
            sx={{ height: 34, borderRadius: 2, textTransform: "none", fontSize: 12, color: "#2f3891", border: `1px solid #c7d2fe`, bgcolor: "#eef2ff", "&:hover": { bgcolor: "#e0e7ff" } }}>
            Clear All Filters
          </Button>
        )}
      </Box>

      {/* ── Filters ── */}
      <Card elevation={0} sx={{ border: `1px solid ${BORDER}`, borderRadius: 2.5, p: 2, mb: 3 }}>
        <Box sx={{ display: "flex", gap: 1.5, alignItems: "center", flexWrap: "wrap" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, color: "#64748b" }}>
            <FilterListIcon sx={{ fontSize: 16 }} />
            <Typography sx={{ fontSize: 12, fontWeight: 600 }}>Filters</Typography>
          </Box>
          <DatePicker
            views={["day"]} openTo="day"
            value={dateFrom ? dayjs(dateFrom) : null}
            onChange={(val: Dayjs | null) => setDateFrom(val && val.isValid() ? val.format("YYYY-MM-DD") : "")}
            format="MMM DD, YYYY"
            slotProps={{
              textField: { size: "small", placeholder: "From date",
                sx: { width: 160, "& .MuiOutlinedInput-root": { borderRadius: 2, bgcolor: "#fff", fontSize: 13, height: 36 } } },
              popper: { sx: {
                "& .MuiPaper-root": { borderRadius: 3, boxShadow: "0 12px 40px rgba(0,0,0,0.12)", border: "1px solid #e2e8f0" },
                "& .MuiPickersDay-root.Mui-selected": { bgcolor: `${BRAND} !important`, fontWeight: 700 },
                "& .MuiPickersDay-root:hover": { bgcolor: "#eef2ff" },
                "& .MuiPickersDay-root": { borderRadius: 2, fontWeight: 500 },
                "& .MuiPickersCalendarHeader-label": { fontWeight: 700, fontSize: 14, color: "#1e293b", pointerEvents: "none" },
                "& .MuiPickersArrowSwitcher-button": { borderRadius: 1.5 },
              }},
            }}
          />
          <DatePicker
            views={["day"]} openTo="day"
            value={dateTo ? dayjs(dateTo) : null}
            minDate={dateFrom ? dayjs(dateFrom) : undefined}
            onChange={(val: Dayjs | null) => setDateTo(val && val.isValid() ? val.format("YYYY-MM-DD") : "")}
            format="MMM DD, YYYY"
            slotProps={{
              textField: { size: "small", placeholder: "To date",
                sx: { width: 160, "& .MuiOutlinedInput-root": { borderRadius: 2, bgcolor: "#fff", fontSize: 13, height: 36 } } },
              popper: { sx: {
                "& .MuiPaper-root": { borderRadius: 3, boxShadow: "0 12px 40px rgba(0,0,0,0.12)", border: "1px solid #e2e8f0" },
                "& .MuiPickersDay-root.Mui-selected": { bgcolor: `${BRAND} !important`, fontWeight: 700 },
                "& .MuiPickersDay-root:hover": { bgcolor: "#eef2ff" },
                "& .MuiPickersDay-root": { borderRadius: 2, fontWeight: 500 },
                "& .MuiPickersCalendarHeader-label": { fontWeight: 700, fontSize: 14, color: "#1e293b", pointerEvents: "none" },
                "& .MuiPickersArrowSwitcher-button": { borderRadius: 1.5 },
              }},
            }}
          />
          <Select size="small" value={paymentMethod} onChange={(e) => { setPaymentMethod(e.target.value); setActiveMethodCard(null); }} displayEmpty
            sx={{ minWidth: 150, borderRadius: 2, fontSize: 13, height: 36, bgcolor: "#fff" }}>
            <MenuItem value=""><em>All Methods</em></MenuItem>
            {ALL_METHODS.map(m => (
              <MenuItem key={m} value={m} sx={{ fontSize: 13 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: METHOD_CFG[m].color }} />
                  {METHOD_CFG[m].label}
                </Box>
              </MenuItem>
            ))}
          </Select>
          {/* Client/invoice search */}
          <Box sx={{ position: "relative", display: "flex", alignItems: "center" }}>
            <SearchIcon sx={{ position: "absolute", left: 9, fontSize: 15, color: "#94a3b8", pointerEvents: "none" }} />
            <input
              value={clientSearch}
              onChange={e => setClientSearch(e.target.value)}
              placeholder="Search client, invoice…"
              style={{ height: 36, border: `1px solid ${BORDER}`, borderRadius: 8, padding: "0 10px 0 28px", fontSize: 13, color: "#374151", outline: "none", minWidth: 190 }}
            />
          </Box>
        </Box>
      </Card>

      {/* ── Summary Cards ── */}
      {summaryLoading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}><CircularProgress size={32} /></Box>
      ) : (
        <Box sx={{ display: "flex", gap: 2, mb: 3, flexWrap: "wrap" }}>
          <Card elevation={0} sx={{ flex: "1 1 200px", border: "2px solid #bbf7d0", borderRadius: 2.5, p: 2.5, bgcolor: "#f0fdf4" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
              <Box sx={{ width: 36, height: 36, borderRadius: 2, bgcolor: "#dcfce7", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <TrendingUpIcon sx={{ fontSize: 18, color: "#16a34a" }} />
              </Box>
              <Typography sx={{ fontSize: 11, fontWeight: 700, color: "#16a34a", textTransform: "uppercase", letterSpacing: "0.05em" }}>Total Income</Typography>
            </Box>
            <Typography sx={{ fontSize: 28, fontWeight: 800, color: "#16a34a", lineHeight: 1 }}>{fmt(effectiveSummary.total_income)}</Typography>
            <Typography sx={{ fontSize: 11, color: "#4ade80", mt: 0.5 }}>{incomeRows.length} payments received</Typography>
          </Card>

          <Card elevation={0} sx={{ flex: "1 1 200px", border: "2px solid #fecaca", borderRadius: 2.5, p: 2.5, bgcolor: "#fef2f2" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
              <Box sx={{ width: 36, height: 36, borderRadius: 2, bgcolor: "#fee2e2", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <TrendingDownIcon sx={{ fontSize: 18, color: "#ef4444" }} />
              </Box>
              <Typography sx={{ fontSize: 11, fontWeight: 700, color: "#ef4444", textTransform: "uppercase", letterSpacing: "0.05em" }}>Total Expenses</Typography>
            </Box>
            <Typography sx={{ fontSize: 28, fontWeight: 800, color: "#ef4444", lineHeight: 1 }}>{fmt(effectiveSummary.total_expense)}</Typography>
            <Typography sx={{ fontSize: 11, color: "#f87171", mt: 0.5 }}>All expense records</Typography>
          </Card>

          <Card elevation={0} sx={{ flex: "1 1 200px", borderRadius: 2.5, p: 2.5, border: `2px solid ${effectiveSummary.net_balance >= 0 ? "#c7d2fe" : "#fecaca"}`, bgcolor: effectiveSummary.net_balance >= 0 ? "#eef2ff" : "#fef2f2" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
              <Box sx={{ width: 36, height: 36, borderRadius: 2, bgcolor: effectiveSummary.net_balance >= 0 ? "#e0e7ff" : "#fee2e2", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <AccountBalanceIcon sx={{ fontSize: 18, color: effectiveSummary.net_balance >= 0 ? BRAND : "#ef4444" }} />
              </Box>
              <Typography sx={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em", color: effectiveSummary.net_balance >= 0 ? BRAND : "#ef4444" }}>Net Balance</Typography>
            </Box>
            <Typography sx={{ fontSize: 28, fontWeight: 800, lineHeight: 1, color: effectiveSummary.net_balance >= 0 ? BRAND : "#ef4444" }}>{fmt(effectiveSummary.net_balance)}</Typography>
            <Typography sx={{ fontSize: 11, mt: 0.5, color: effectiveSummary.net_balance >= 0 ? "#818cf8" : "#f87171" }}>{effectiveSummary.net_balance >= 0 ? "Profitable" : "Net loss"}</Typography>
          </Card>
        </Box>
      )}

      {/* ── Smart Insights ── */}
      {smartInsights && !incomeLoading && (
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 1.25 }}>
            <LightbulbOutlinedIcon sx={{ fontSize: 15, color: "#f59e0b" }} />
            <Typography sx={{ fontSize: 12, fontWeight: 700, color: "#0f172a" }}>Smart Insights</Typography>
          </Box>
          <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap" }}>
            <Box sx={{ flex: "1 1 220px", display: "flex", alignItems: "center", gap: 1.25, px: 2, py: 1.25, bgcolor: "#fffbeb", border: "1px solid #fde68a", borderRadius: 2, cursor: "pointer", transition: "all 0.15s", "&:hover": { transform: "translateY(-1px)", boxShadow: "0 4px 12px rgba(245,158,11,0.15)" } }}
              onClick={() => handleCardClick(smartInsights.mostUsed)}>
              <Box sx={{ width: 30, height: 30, borderRadius: "50%", bgcolor: "#fef3c7", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14, flexShrink: 0 }}>
                {METHOD_CFG[smartInsights.mostUsed]?.icon}
              </Box>
              <Box>
                <Typography sx={{ fontSize: 10, fontWeight: 700, color: "#92400e", textTransform: "uppercase", letterSpacing: "0.06em" }}>Most Used Method</Typography>
                <Typography sx={{ fontSize: 13, fontWeight: 800, color: "#78350f" }}>
                  {METHOD_CFG[smartInsights.mostUsed]?.label} — {methodStats[smartInsights.mostUsed]?.count} transactions
                </Typography>
              </Box>
            </Box>
            <Box sx={{ flex: "1 1 220px", display: "flex", alignItems: "center", gap: 1.25, px: 2, py: 1.25, bgcolor: "#f0fdf4", border: "1px solid #bbf7d0", borderRadius: 2, cursor: "pointer", transition: "all 0.15s", "&:hover": { transform: "translateY(-1px)", boxShadow: "0 4px 12px rgba(22,163,74,0.15)" } }}
              onClick={() => handleCardClick(smartInsights.highestEarn)}>
              <EmojiEventsOutlinedIcon sx={{ fontSize: 22, color: "#16a34a", flexShrink: 0 }} />
              <Box>
                <Typography sx={{ fontSize: 10, fontWeight: 700, color: "#166534", textTransform: "uppercase", letterSpacing: "0.06em" }}>Highest Earning Method</Typography>
                <Typography sx={{ fontSize: 13, fontWeight: 800, color: "#14532d" }}>
                  {METHOD_CFG[smartInsights.highestEarn]?.label} — {fmt(methodStats[smartInsights.highestEarn]?.income ?? 0)}
                </Typography>
              </Box>
            </Box>
            {smartInsights.lowestEarn && (
              <Box sx={{ flex: "1 1 220px", display: "flex", alignItems: "center", gap: 1.25, px: 2, py: 1.25, bgcolor: "#fef2f2", border: "1px solid #fecaca", borderRadius: 2, cursor: "pointer", transition: "all 0.15s", "&:hover": { transform: "translateY(-1px)", boxShadow: "0 4px 12px rgba(239,68,68,0.15)" } }}
                onClick={() => handleCardClick(smartInsights.lowestEarn!)}>
                <ShowChartIcon sx={{ fontSize: 22, color: "#ef4444", flexShrink: 0 }} />
                <Box>
                  <Typography sx={{ fontSize: 10, fontWeight: 700, color: "#991b1b", textTransform: "uppercase", letterSpacing: "0.06em" }}>Lowest Earning Method</Typography>
                  <Typography sx={{ fontSize: 13, fontWeight: 800, color: "#7f1d1d" }}>
                    {METHOD_CFG[smartInsights.lowestEarn]?.label} — {fmt(methodStats[smartInsights.lowestEarn]?.income ?? 0)}
                  </Typography>
                </Box>
              </Box>
            )}
            <Box sx={{ flex: "1 1 220px", display: "flex", alignItems: "center", gap: 1.25, px: 2, py: 1.25, bgcolor: "#f0f9ff", border: "1px solid #bae6fd", borderRadius: 2 }}>
              <PersonOutlineIcon sx={{ fontSize: 22, color: "#0ea5e9", flexShrink: 0 }} />
              <Box>
                <Typography sx={{ fontSize: 10, fontWeight: 700, color: "#075985", textTransform: "uppercase", letterSpacing: "0.06em" }}>Top Paying Client</Typography>
                <Typography sx={{ fontSize: 13, fontWeight: 800, color: "#0c4a6e" }} noWrap>
                  {advanced.insights.top_client_name} {advanced.insights.top_client_amount > 0 && `— ${fmt(advanced.insights.top_client_amount)}`}
                </Typography>
              </Box>
            </Box>
          </Box>
        </Box>
      )}

      {/* ── Quick Insights Cards ── */}
      {!advancedLoading && (
        <Box sx={{ display: "flex", gap: 2, mb: 3, flexWrap: "wrap" }}>
          {[
            { icon: <CalendarMonthOutlinedIcon sx={{ fontSize: 18, color: "#2f3891" }} />, label: "This Month Profit", value: fmt(advanced.insights.this_month_profit), sub: dayjs().format("MMMM YYYY"), color: advanced.insights.this_month_profit >= 0 ? "#2f3891" : "#ef4444", bg: advanced.insights.this_month_profit >= 0 ? "#eef2ff" : "#fef2f2", border: advanced.insights.this_month_profit >= 0 ? "#c7d2fe" : "#fecaca" },
            { icon: <CategoryOutlinedIcon sx={{ fontSize: 18, color: "#f59e0b" }} />, label: "Top Expense Category", value: advanced.insights.top_expense_category, sub: fmt(advanced.insights.top_expense_amount) + " total", color: "#f59e0b", bg: "#fffbeb", border: "#fde68a" },
          ].map((card, i) => (
            <Card key={i} elevation={0} sx={{ flex: "1 1 180px", border: `1px solid ${card.border}`, borderRadius: 2.5, p: 2, bgcolor: card.bg }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.75 }}>
                <Box sx={{ width: 30, height: 30, borderRadius: 1.5, bgcolor: "#fff", display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "0 1px 4px rgba(0,0,0,0.08)" }}>{card.icon}</Box>
                <Typography sx={{ fontSize: 10, fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.05em" }}>{card.label}</Typography>
              </Box>
              <Typography sx={{ fontSize: 16, fontWeight: 800, color: card.color, lineHeight: 1.2 }} noWrap>{card.value}</Typography>
              <Typography sx={{ fontSize: 10, color: "#94a3b8", mt: 0.4 }}>{card.sub}</Typography>
            </Card>
          ))}
        </Box>
      )}

      {/* ── Charts Row ── */}
      {advancedLoading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}><CircularProgress size={32} /></Box>
      ) : (
        <Box sx={{ display: "flex", gap: 2, mb: 3, flexWrap: "wrap" }}>
          <Card elevation={0} sx={{ flex: "2 1 400px", border: `1px solid ${BORDER}`, borderRadius: 2.5, p: 2.5 }}>
            <SectionTitle title="Monthly Profit Overview" subtitle="Income vs Expenses vs Profit per month" />
            {advanced.monthly.length === 0 ? (
              <Box sx={{ height: 240, display: "flex", alignItems: "center", justifyContent: "center" }}>
                <Typography sx={{ fontSize: 13, color: "#94a3b8" }}>No data for selected period</Typography>
              </Box>
            ) : <HighchartsReact highcharts={Highcharts} options={monthlyChartOpts} />}
          </Card>
          <Card elevation={0} sx={{ flex: "1 1 260px", border: `1px solid ${BORDER}`, borderRadius: 2.5, p: 2.5 }}>
            <SectionTitle title="Invoice Payment Status" subtitle="Paid vs Outstanding vs Partial" />
            {advanced.invoice_status.paid + advanced.invoice_status.outstanding + advanced.invoice_status.partial === 0 ? (
              <Box sx={{ height: 180, display: "flex", alignItems: "center", justifyContent: "center" }}>
                <Typography sx={{ fontSize: 13, color: "#94a3b8" }}>No invoice data</Typography>
              </Box>
            ) : (
              <>
                <HighchartsReact highcharts={Highcharts} options={invoiceDonutOpts} />
                <Box sx={{ display: "flex", gap: 1, mt: 1, flexWrap: "wrap" }}>
                  {[
                    { label: "Paid",        value: advanced.invoice_status.paid,        color: "#16a34a", bg: "#f0fdf4" },
                    { label: "Outstanding", value: advanced.invoice_status.outstanding, color: "#ef4444", bg: "#fef2f2" },
                    { label: "Partial",     value: advanced.invoice_status.partial,     color: "#f59e0b", bg: "#fffbeb" },
                  ].map(s => (
                    <Box key={s.label} sx={{ flex: 1, minWidth: 80, p: 1, borderRadius: 1.5, bgcolor: s.bg, textAlign: "center" }}>
                      <Typography sx={{ fontSize: 9, fontWeight: 700, color: s.color, textTransform: "uppercase" }}>{s.label}</Typography>
                      <Typography sx={{ fontSize: 12, fontWeight: 800, color: s.color }}>{fmt(s.value)}</Typography>
                    </Box>
                  ))}
                </Box>
              </>
            )}
          </Card>
        </Box>
      )}

      {/* ── HST / Tax Summary ── */}
      {!advancedLoading && (
        <Box sx={{ mb: 3 }}>
          <SectionTitle title="HST / Tax Summary" subtitle="Tax collected from invoices vs tax paid on expenses" />
          <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
            {[
              { label: "Tax Collected", value: advanced.tax.collected, sub: "From invoice HST", color: "#16a34a", bg: "#f0fdf4", border: "#bbf7d0" },
              { label: "Tax Paid",      value: advanced.tax.paid,      sub: "From expense HST", color: "#ef4444", bg: "#fef2f2", border: "#fecaca" },
              { label: "Net Tax Payable", value: advanced.tax.net, sub: advanced.tax.net > 0 ? "Amount owed to CRA" : advanced.tax.net < 0 ? "Refund eligible" : "Balanced", color: advanced.tax.net > 0 ? "#f59e0b" : advanced.tax.net < 0 ? "#2f3891" : "#16a34a", bg: advanced.tax.net > 0 ? "#fffbeb" : advanced.tax.net < 0 ? "#eef2ff" : "#f0fdf4", border: advanced.tax.net > 0 ? "#fde68a" : advanced.tax.net < 0 ? "#c7d2fe" : "#bbf7d0" },
            ].map(card => (
              <Card key={card.label} elevation={0} sx={{ flex: "1 1 180px", border: `1px solid ${card.border}`, borderRadius: 2, p: 2, bgcolor: card.bg }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 1 }}>
                  <ReceiptLongOutlinedIcon sx={{ fontSize: 15, color: card.color }} />
                  <Typography sx={{ fontSize: 10, fontWeight: 700, color: card.color, textTransform: "uppercase", letterSpacing: "0.05em" }}>{card.label}</Typography>
                </Box>
                <Typography sx={{ fontSize: 24, fontWeight: 800, color: card.color, lineHeight: 1 }}>{fmt(card.value)}</Typography>
                <Typography sx={{ fontSize: 10, color: "#94a3b8", mt: 0.5 }}>{card.sub}</Typography>
              </Card>
            ))}
          </Box>
        </Box>
      )}

      {/* ── INTERACTIVE Payment Method Breakdown ── */}
      {!summaryLoading && (
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1.25 }}>
            <Box>
              <SectionTitle title="Payment Method Breakdown" subtitle="Click a card to filter Income Records by method" />
            </Box>
            {activeMethodCard && (
              <Button size="small" onClick={() => setActiveMethodCard(null)}
                sx={{ fontSize: 11, textTransform: "none", color: "#64748b", border: `1px solid ${BORDER}`, borderRadius: 2, height: 28, px: 1.5 }}>
                Show All
              </Button>
            )}
          </Box>
          <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap" }}>
            {ALL_METHODS.map(method => {
              const cfg     = METHOD_CFG[method];
              const brkData = summary.breakdown?.[method] ?? { income: 0, expense: 0, balance: 0 };
              const stats   = methodStats[method];
              const isActive = activeMethodCard === method;
              return (
                <Tooltip key={method} title={isActive ? `Click to deselect ${cfg.label}` : `Click to filter by ${cfg.label}`} placement="top">
                  <Card
                    elevation={0}
                    onClick={() => handleCardClick(method)}
                    sx={{
                      flex: "1 1 140px", borderRadius: 2.5, p: 2, cursor: "pointer",
                      border: isActive ? `2px solid ${cfg.activeBorder}` : `1px solid ${cfg.border}`,
                      bgcolor: isActive ? cfg.bg : "#fff",
                      boxShadow: isActive ? `0 0 0 4px ${cfg.glow}, 0 4px 16px ${cfg.glow}` : "0 1px 3px rgba(0,0,0,0.04)",
                      transform: isActive ? "translateY(-3px) scale(1.02)" : "none",
                      transition: "all 0.2s cubic-bezier(0.4,0,0.2,1)",
                      position: "relative",
                      overflow: "visible",
                      "&:hover": {
                        border: `2px solid ${cfg.activeBorder}`,
                        boxShadow: `0 0 0 3px ${cfg.glow}, 0 6px 20px ${cfg.glow}`,
                        transform: "translateY(-2px)",
                        bgcolor: cfg.bg,
                      },
                    }}
                  >
                    {/* Active indicator dot */}
                    {isActive && (
                      <Box sx={{ position: "absolute", top: -5, right: -5, width: 14, height: 14, borderRadius: "50%", bgcolor: cfg.color, border: "2px solid #fff", display: "flex", alignItems: "center", justifyContent: "center" }}>
                        <Box sx={{ width: 5, height: 5, borderRadius: "50%", bgcolor: "#fff" }} />
                      </Box>
                    )}
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 1 }}>
                      <Box sx={{ fontSize: 16 }}>{cfg.icon}</Box>
                      <Typography sx={{ fontSize: 12, fontWeight: 700, color: isActive ? cfg.color : "#374151" }}>{cfg.label}</Typography>
                    </Box>
                    <Divider sx={{ mb: 1, borderColor: isActive ? cfg.border : "#f1f5f9" }} />
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                      <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                        <Typography sx={{ fontSize: 10, color: "#64748b" }}>Income</Typography>
                        <Typography sx={{ fontSize: 11, fontWeight: 700, color: "#16a34a" }}>{fmt(brkData.income)}</Typography>
                      </Box>
                      <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                        <Typography sx={{ fontSize: 10, color: "#64748b" }}>Balance</Typography>
                        <Typography sx={{ fontSize: 11, fontWeight: 700, color: brkData.balance >= 0 ? BRAND : "#ef4444" }}>{fmt(brkData.balance)}</Typography>
                      </Box>
                      <Box sx={{ display: "flex", justifyContent: "space-between", pt: 0.5, borderTop: "1px solid #f1f5f9" }}>
                        <Typography sx={{ fontSize: 10, color: "#94a3b8" }}>Transactions</Typography>
                        <Box sx={{ display: "inline-flex", alignItems: "center", px: 0.75, py: 0.1, borderRadius: 1, bgcolor: isActive ? cfg.bg : "#f8fafc", border: `1px solid ${isActive ? cfg.border : "#e2e8f0"}` }}>
                          <Typography sx={{ fontSize: 10, fontWeight: 800, color: isActive ? cfg.color : "#64748b" }}>{stats.count}</Typography>
                        </Box>
                      </Box>
                    </Box>
                  </Card>
                </Tooltip>
              );
            })}
          </Box>
        </Box>
      )}

      {/* ── Tabs ── */}
      <Box sx={{ display: "flex", gap: 0, borderBottom: `2px solid ${BORDER}`, mb: 0 }}>
        {(["income", "expenses", "transactions", "profit"] as const).map(tab => (
          <Button key={tab} size="small" onClick={() => setActiveTab(tab)}
            sx={{
              textTransform: "none", fontSize: 13, fontWeight: 600, borderRadius: 0,
              pb: 1.25, px: 2.5,
              borderBottom: activeTab === tab ? `2px solid ${BRAND}` : "2px solid transparent",
              mb: "-2px", color: activeTab === tab ? BRAND : "#64748b",
              "&:hover": { bgcolor: "#f1f5f9" },
            }}>
            {tab === "profit" ? "Profit Analysis" : tab.charAt(0).toUpperCase() + tab.slice(1)}
            {tab === "income" && activeMethodCard && (
              <Box component="span" sx={{ ml: 0.75, px: 0.75, py: 0.1, borderRadius: 1, fontSize: 10, fontWeight: 800, bgcolor: BRAND, color: "#fff" }}>
                {incomeRows.length}
              </Box>
            )}
          </Button>
        ))}
      </Box>

      {/* ── Income Table ── */}
      {activeTab === "income" && (
        <Card elevation={0} sx={{ border: `1px solid ${BORDER}`, borderRadius: "0 0 12px 12px", overflow: "hidden" }}>
          {/* Drill-down header */}
          {(activeMethodCard || clientSearch) && (
            <Fade in>
              <Box sx={{ px: 2.5, py: 1.25, bgcolor: activeMethodCard ? alpha(METHOD_CFG[activeMethodCard]?.color ?? BRAND, 0.06) : "#f0f9ff", borderBottom: `1px solid ${activeMethodCard ? METHOD_CFG[activeMethodCard]?.border ?? BORDER : "#bae6fd"}`, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <FilterListIcon sx={{ fontSize: 14, color: activeMethodCard ? METHOD_CFG[activeMethodCard]?.color ?? BRAND : "#0ea5e9" }} />
                  <Typography sx={{ fontSize: 12, fontWeight: 700, color: activeMethodCard ? METHOD_CFG[activeMethodCard]?.color ?? BRAND : "#0ea5e9" }}>
                    Showing:{" "}
                    {activeMethodCard ? `${METHOD_CFG[activeMethodCard]?.icon} ${METHOD_CFG[activeMethodCard]?.label} Transactions` : ""}
                    {clientSearch ? ` matching "${clientSearch}"` : ""}
                    {" "}
                    <Box component="span" sx={{ fontWeight: 900 }}>({incomeRows.length} record{incomeRows.length !== 1 ? "s" : ""})</Box>
                  </Typography>
                </Box>
                <Button size="small" onClick={() => { setActiveMethodCard(null); setClientSearch(""); }}
                  sx={{ fontSize: 10, textTransform: "none", color: "#64748b", height: 24, px: 1 }}>
                  Clear
                </Button>
              </Box>
            </Fade>
          )}
          <Box sx={{ px: 2, py: 1.5, display: "flex", alignItems: "center", justifyContent: "space-between", bgcolor: "#fff", borderBottom: `1px solid ${BORDER}` }}>
            <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#0f172a" }}>
              Income Records
              {!incomeLoading && <Box component="span" sx={{ ml: 1, fontSize: 11, fontWeight: 500, color: "#64748b" }}>({incomeRows.length}{allIncomeRows.length !== incomeRows.length ? ` of ${allIncomeRows.length}` : ""})</Box>}
            </Typography>
            <Box sx={{ display: "flex", gap: 1 }}>
              <Button size="small" variant="outlined" startIcon={<TableChartOutlinedIcon sx={{ fontSize: 14 }} />} onClick={handleExportIncomeExcel}
                sx={{ height: 32, borderRadius: 2, textTransform: "none", fontSize: 11, color: "#16a34a", borderColor: "#16a34a", "&:hover": { bgcolor: "#f0fdf4" } }}>
                Excel {(activeMethodCard || clientSearch) && <Box component="span" sx={{ ml: 0.5, fontSize: 9 }}>(filtered)</Box>}
              </Button>
              <Button size="small" variant="outlined"
                startIcon={pdfLoading ? <CircularProgress size={13} color="inherit" /> : <PictureAsPdfOutlinedIcon sx={{ fontSize: 14 }} />}
                onClick={handleDownloadPdf} disabled={pdfLoading}
                sx={{ height: 32, borderRadius: 2, textTransform: "none", fontSize: 11, color: "#ef4444", borderColor: "#ef4444", "&:hover": { bgcolor: "#fef2f2" } }}>
                {pdfLoading ? "Generating…" : "PDF"}
              </Button>
            </Box>
          </Box>
          <TableContainer sx={{ maxHeight: "calc(100vh - 520px)", overflowY: "auto" }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  {["#", "Invoice No", "Work Order", "Client", "Date", "Amount", "Method", "Description"].map(h => (
                    <TableCell key={h} sx={thSx}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {incomeLoading ? (
                  <TableRow><TableCell colSpan={8} sx={{ textAlign: "center", py: 6 }}><CircularProgress size={28} /></TableCell></TableRow>
                ) : incomeRows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} sx={{ textAlign: "center", py: 6 }}>
                      <Typography sx={{ fontSize: 13, color: "#94a3b8" }}>
                        {activeMethodCard ? `No ${METHOD_CFG[activeMethodCard]?.label} transactions found` : "No income records found"}
                      </Typography>
                      {(activeMethodCard || clientSearch) && (
                        <Button size="small" onClick={() => { setActiveMethodCard(null); setClientSearch(""); }} sx={{ mt: 1, fontSize: 11, textTransform: "none", color: BRAND }}>
                          Clear filter
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ) : incomeRows.map((row, i) => (
                  <TableRow
                    key={row.id}
                    onClick={() => setSelectedInvoice(row)}
                    sx={{
                      cursor: "pointer",
                      transition: "background 0.12s",
                      "&:hover": {
                        bgcolor: activeMethodCard ? alpha(METHOD_CFG[activeMethodCard]?.color ?? BRAND, 0.05) : "#f0fdf4",
                        "& .inv-link": { color: BRAND, textDecoration: "underline" },
                      },
                    }}
                  >
                    <TableCell sx={{ ...tdSx, color: "#94a3b8", width: 36 }}>{i + 1}</TableCell>
                    <TableCell sx={tdSx}>
                      <Typography className="inv-link" sx={{ fontSize: 11, fontWeight: 700, color: BRAND, fontFamily: "monospace", transition: "color 0.1s" }}>
                        {row.InvoiceNo || "—"}
                      </Typography>
                    </TableCell>
                    <TableCell sx={tdSx}><Typography sx={{ fontSize: 11, color: "#374151" }}>{row.quoteWorkOrderNo || "—"}</Typography></TableCell>
                    <TableCell sx={tdSx}><Typography sx={{ fontSize: 12, fontWeight: 600, color: "#0f172a" }}>{row.client_name?.trim() || "—"}</Typography></TableCell>
                    <TableCell sx={tdSx}><Typography sx={{ fontSize: 11, color: "#374151" }}>{row.payment_date ? dayjs(row.payment_date).format("DD MMM YYYY") : "—"}</Typography></TableCell>
                    <TableCell sx={tdSx}><Typography sx={{ fontSize: 12, fontWeight: 700, color: "#16a34a" }}>{fmt(row.amount)}</Typography></TableCell>
                    <TableCell sx={tdSx}><MethodChip method={row.payment_method} /></TableCell>
                    <TableCell sx={tdSx}><Typography sx={{ fontSize: 11, color: "#64748b" }} noWrap>{row.description || "—"}</Typography></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          {incomeRows.length > 0 && (
            <Box sx={{ px: 2, py: 1.25, borderTop: `1px solid ${BORDER}`, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <Typography sx={{ fontSize: 12, color: "#64748b" }}>
                {incomeRows.length} record{incomeRows.length !== 1 ? "s" : ""}
                {filterNote && <Box component="span" sx={{ ml: 1, color: BRAND, fontSize: 11 }}>· {filterNote}</Box>}
              </Typography>
              <Typography sx={{ fontSize: 13, fontWeight: 800, color: "#16a34a" }}>
                Total: {fmt(incomeRows.reduce((s, r) => s + (+r.amount || 0), 0))}
              </Typography>
            </Box>
          )}
        </Card>
      )}

      {/* ── Expenses Table ── */}
      {activeTab === "expenses" && (
        <Card elevation={0} sx={{ border: `1px solid ${BORDER}`, borderRadius: "0 0 12px 12px", overflow: "hidden" }}>
          <Box sx={{ px: 2, py: 1.5, display: "flex", alignItems: "center", justifyContent: "space-between", bgcolor: "#fff", borderBottom: `1px solid ${BORDER}` }}>
            <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#0f172a" }}>
              Expense Records
              {!expensesLoading && <Box component="span" sx={{ ml: 1, fontSize: 11, fontWeight: 500, color: "#64748b" }}>({expenseRows.length})</Box>}
            </Typography>
            <Box sx={{ display: "flex", gap: 1 }}>
              <Button size="small" variant="outlined" startIcon={<TableChartOutlinedIcon sx={{ fontSize: 14 }} />} onClick={handleExportExpensesExcel}
                sx={{ height: 32, borderRadius: 2, textTransform: "none", fontSize: 11, color: "#16a34a", borderColor: "#16a34a", "&:hover": { bgcolor: "#f0fdf4" } }}>Excel</Button>
              <Button size="small" variant="outlined"
                startIcon={pdfLoading ? <CircularProgress size={13} color="inherit" /> : <PictureAsPdfOutlinedIcon sx={{ fontSize: 14 }} />}
                onClick={handleDownloadPdf} disabled={pdfLoading}
                sx={{ height: 32, borderRadius: 2, textTransform: "none", fontSize: 11, color: "#ef4444", borderColor: "#ef4444", "&:hover": { bgcolor: "#fef2f2" } }}>
                {pdfLoading ? "Generating…" : "PDF"}
              </Button>
            </Box>
          </Box>
          <TableContainer sx={{ maxHeight: "calc(100vh - 480px)", overflowY: "auto" }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  {["#", "Expense ID", "Category", "Description", "Date", "Amount", "HST", "Total", "Status"].map(h => (
                    <TableCell key={h} sx={thSx}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {expensesLoading ? (
                  <TableRow><TableCell colSpan={9} sx={{ textAlign: "center", py: 6 }}><CircularProgress size={28} /></TableCell></TableRow>
                ) : expenseRows.length === 0 ? (
                  <TableRow><TableCell colSpan={9} sx={{ textAlign: "center", py: 6 }}><Typography sx={{ fontSize: 13, color: "#94a3b8" }}>No expense records found</Typography></TableCell></TableRow>
                ) : expenseRows.map((row, i) => {
                  const sc = row.status === "paid" ? "#16a34a" : row.status === "unpaid" ? "#ef4444" : "#f59e0b";
                  const sb = row.status === "paid" ? "#f0fdf4"  : row.status === "unpaid" ? "#fef2f2"  : "#fffbeb";
                  return (
                    <TableRow key={row.id} sx={{ "&:hover": { bgcolor: "#fef2f2" } }}>
                      <TableCell sx={{ ...tdSx, color: "#94a3b8", width: 36 }}>{i + 1}</TableCell>
                      <TableCell sx={tdSx}><Typography sx={{ fontSize: 11, fontWeight: 700, color: "#ef4444", fontFamily: "monospace" }}>EXP-{row.id}</Typography></TableCell>
                      <TableCell sx={tdSx}><Box component="span" sx={{ px: 1, py: 0.2, borderRadius: 1, fontSize: 10, fontWeight: 700, bgcolor: "#f1f5f9", color: "#64748b" }}>{row.category || "—"}</Box></TableCell>
                      <TableCell sx={tdSx}><Typography sx={{ fontSize: 12, color: "#374151" }} noWrap>{row.description}</Typography></TableCell>
                      <TableCell sx={tdSx}><Typography sx={{ fontSize: 11, color: "#374151" }}>{row.date ? dayjs(row.date).format("DD MMM YYYY") : "—"}</Typography></TableCell>
                      <TableCell sx={tdSx}><Typography sx={{ fontSize: 11, color: "#374151" }}>{fmt(row.amount)}</Typography></TableCell>
                      <TableCell sx={tdSx}><Typography sx={{ fontSize: 11, color: "#64748b" }}>{row.hst ? fmt(row.hst) : "—"}</Typography></TableCell>
                      <TableCell sx={tdSx}><Typography sx={{ fontSize: 12, fontWeight: 700, color: "#ef4444" }}>{fmt(row.total ?? row.amount)}</Typography></TableCell>
                      <TableCell sx={tdSx}>
                        {row.status
                          ? <Box component="span" sx={{ px: 1.25, py: 0.3, borderRadius: 1.5, fontSize: 11, fontWeight: 700, color: sc, bgcolor: sb }}>{row.status.charAt(0).toUpperCase() + row.status.slice(1)}</Box>
                          : <Typography sx={{ fontSize: 11, color: "#cbd5e1" }}>—</Typography>}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
          {expenseRows.length > 0 && (
            <Box sx={{ px: 2, py: 1.25, borderTop: `1px solid ${BORDER}`, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <Typography sx={{ fontSize: 12, color: "#64748b" }}>{expenseRows.length} record{expenseRows.length !== 1 ? "s" : ""}</Typography>
              <Typography sx={{ fontSize: 13, fontWeight: 800, color: "#ef4444" }}>Total: {fmt(expenseRows.reduce((s, r) => s + (+(r.total ?? r.amount) || 0), 0))}</Typography>
            </Box>
          )}
        </Card>
      )}

      {/* ── Transactions Table ── */}
      {activeTab === "transactions" && (
        <Card elevation={0} sx={{ border: `1px solid ${BORDER}`, borderRadius: "0 0 12px 12px", overflow: "hidden" }}>
          <Box sx={{ px: 2, py: 1.5, display: "flex", alignItems: "center", justifyContent: "space-between", bgcolor: "#fff", borderBottom: `1px solid ${BORDER}` }}>
            <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#0f172a" }}>
              Transaction Ledger
              {!txLoading && <Box component="span" sx={{ ml: 1, fontSize: 11, fontWeight: 500, color: "#64748b" }}>({txRows.length})</Box>}
            </Typography>
            {txRows.length > 0 && (
              <Button size="small" variant="outlined" startIcon={<TableChartOutlinedIcon sx={{ fontSize: 14 }} />} onClick={handleExportTransactionsExcel}
                sx={{ height: 32, borderRadius: 2, textTransform: "none", fontSize: 11, color: "#16a34a", borderColor: "#bbf7d0", "&:hover": { bgcolor: "#f0fdf4" } }}>
                Excel
              </Button>
            )}
            <Button size="small" variant="outlined"
              startIcon={pdfLoading ? <CircularProgress size={13} color="inherit" /> : <PictureAsPdfOutlinedIcon sx={{ fontSize: 14 }} />}
              onClick={handleDownloadPdf} disabled={pdfLoading}
              sx={{ height: 32, borderRadius: 2, textTransform: "none", fontSize: 11, color: "#ef4444", borderColor: "#ef4444", "&:hover": { bgcolor: "#fef2f2" } }}>
              {pdfLoading ? "Generating…" : "PDF"}
            </Button>
          </Box>
          <TableContainer sx={{ maxHeight: "calc(100vh - 480px)", overflowY: "auto" }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  {["#", "Date", "Type", "Reference", "Method", "Amount", "Running Balance", "Description"].map(h => (
                    <TableCell key={h} sx={thSx}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {txLoading ? (
                  <TableRow><TableCell colSpan={8} sx={{ textAlign: "center", py: 6 }}><CircularProgress size={28} /></TableCell></TableRow>
                ) : txRows.length === 0 ? (
                  <TableRow><TableCell colSpan={8} sx={{ textAlign: "center", py: 6 }}><Typography sx={{ fontSize: 13, color: "#94a3b8" }}>No transactions found</Typography></TableCell></TableRow>
                ) : txRows.map((row, i) => {
                  const isIncome = row.type === "income";
                  return (
                    <TableRow key={`${row.type}-${row.id}-${i}`}
                      onClick={() => setSelectedTx(row)}
                      sx={{ cursor: "pointer", "&:hover": { bgcolor: isIncome ? "#f0fdf4" : "#fef2f2" } }}>
                      <TableCell sx={{ ...tdSx, color: "#94a3b8", width: 36 }}>{i + 1}</TableCell>
                      <TableCell sx={tdSx}><Typography sx={{ fontSize: 11, color: "#374151" }}>{row.date ? dayjs(row.date).format("DD MMM YYYY") : "—"}</Typography></TableCell>
                      <TableCell sx={tdSx}><Box component="span" sx={{ px: 1, py: 0.2, borderRadius: 1, fontSize: 10, fontWeight: 700, color: isIncome ? "#16a34a" : "#ef4444", bgcolor: isIncome ? "#f0fdf4" : "#fef2f2" }}>{isIncome ? "Income" : "Expense"}</Box></TableCell>
                      <TableCell sx={tdSx}><Typography sx={{ fontSize: 11, fontWeight: 600, color: BRAND, fontFamily: "monospace" }}>{row.reference || "—"}</Typography></TableCell>
                      <TableCell sx={tdSx}><MethodChip method={row.method} /></TableCell>
                      <TableCell sx={tdSx}><Typography sx={{ fontSize: 12, fontWeight: 700, color: isIncome ? "#16a34a" : "#ef4444" }}>{isIncome ? "+" : "−"}{fmt(row.amount)}</Typography></TableCell>
                      <TableCell sx={tdSx}><Typography sx={{ fontSize: 12, fontWeight: 700, color: row.balance_after >= 0 ? BRAND : "#ef4444" }}>{fmt(row.balance_after)}</Typography></TableCell>
                      <TableCell sx={tdSx}><Typography sx={{ fontSize: 11, color: "#64748b" }} noWrap>{row.description || "—"}</Typography></TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
          {txRows.length > 0 && (
            <Box sx={{ px: 2, py: 1.25, borderTop: `1px solid ${BORDER}`, display: "flex", justifyContent: "flex-end", alignItems: "center" }}>
              <Typography sx={{ fontSize: 12, color: "#64748b", mr: 2 }}>{txRows.length} transactions</Typography>
              <Typography sx={{ fontSize: 13, fontWeight: 800, color: txRows[txRows.length - 1]?.balance_after >= 0 ? BRAND : "#ef4444" }}>
                Final Balance: {fmt(txRows[txRows.length - 1]?.balance_after ?? 0)}
              </Typography>
            </Box>
          )}
        </Card>
      )}

      {/* ── Profit Analysis Tab ── */}
      {activeTab === "profit" && (<>
        <Card elevation={0} sx={{ border: `1px solid ${BORDER}`, borderRadius: "0 0 12px 12px", overflow: "hidden" }}>
          <Box sx={{ p: 2.5, bgcolor: "#f8fafc", borderBottom: `1px solid ${BORDER}`, display: "flex", gap: 2, flexWrap: "wrap" }}>
            {[
              { label: "Total Revenue",     value: tripProfitSummary.total_revenue,     color: "#2f3891" },
              { label: "Total Driver Cost", value: tripProfitSummary.total_driver_cost, color: "#f59e0b" },
              { label: "Total Expenses",    value: tripProfitSummary.total_expenses,    color: "#ef4444" },
              { label: "Total Profit",      value: tripProfitSummary.total_profit,      color: tripProfitSummary.total_profit >= 0 ? "#16a34a" : "#dc2626" },
            ].map((item, i) => (
              <Card key={i} elevation={0} sx={{ flex: "1 1 180px", border: `1px solid ${BORDER}`, borderRadius: 2, p: 2 }}>
                <Typography sx={{ fontSize: 10, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.07em" }}>{item.label}</Typography>
                <Typography sx={{ fontSize: 20, fontWeight: 800, color: item.color, mt: 0.5 }}>{fmt(item.value)}</Typography>
                {i === 3 && tripProfitSummary.profit_margin !== 0 && (
                  <Typography sx={{ fontSize: 11, color: "#94a3b8", mt: 0.25 }}>{tripProfitSummary.profit_margin}% margin</Typography>
                )}
              </Card>
            ))}
          </Box>
          {tripProfitMonthly.length > 0 && (
            <Box sx={{ p: 2.5, borderBottom: `1px solid ${BORDER}` }}>
              <SectionTitle title="Monthly Profit Trend (Per Trip)" subtitle="Revenue vs Driver Cost vs Net Profit by month" />
              <HighchartsReact highcharts={Highcharts} options={tripProfitChartOpts} />
            </Box>
          )}
          <Box sx={{ px: 2, py: 1.5, display: "flex", alignItems: "center", justifyContent: "space-between", borderBottom: `1px solid ${BORDER}` }}>
            <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#0f172a" }}>
              Profit Per Trip
              {!profitLoading && <Box component="span" sx={{ ml: 1, fontSize: 11, fontWeight: 500, color: "#64748b" }}>({tripProfitRows.length})</Box>}
            </Typography>
            <Button size="small" variant="outlined" startIcon={<FileDownloadOutlinedIcon sx={{ fontSize: 14 }} />}
              onClick={() => exportCSV(tripProfitRows, `profit_per_trip_${dayjs().format("YYYY-MM-DD")}.csv`, ["Trip ID", "Work Order", "Invoice", "Client", "Date", "Revenue", "Driver Cost", "Expenses", "Profit", "Margin %"], ["trip_id", "work_order_no", "invoice_no", "client_name", "trip_date", "revenue", "driver_cost", "trip_expenses", "profit", "profit_margin"])}
              sx={{ fontSize: 11, borderColor: BORDER, color: "#64748b", borderRadius: 2 }}>CSV</Button>
          </Box>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>{["#", "Work Order", "Invoice", "Client", "Date", "Revenue", "Driver Cost", "Expenses", "Profit", "Margin"].map(h => <TableCell key={h} sx={thSx}>{h}</TableCell>)}</TableRow>
              </TableHead>
              <TableBody>
                {profitLoading ? (
                  <TableRow><TableCell colSpan={10} sx={{ textAlign: "center", py: 5 }}><CircularProgress size={24} /></TableCell></TableRow>
                ) : tripProfitRows.length === 0 ? (
                  <TableRow><TableCell colSpan={10} sx={{ textAlign: "center", py: 6 }}><Typography sx={{ fontSize: 13, color: "#94a3b8" }}>No trip data found</Typography></TableCell></TableRow>
                ) : tripProfitRows.map((row, i) => {
                  const pc = row.profit > 0 ? "#16a34a" : row.profit < 0 ? "#dc2626" : "#f59e0b";
                  const pb = row.profit > 0 ? "rgba(22,163,74,0.07)" : row.profit < 0 ? "rgba(220,38,38,0.07)" : "rgba(245,158,11,0.07)";
                  return (
                    <TableRow key={row.trip_id} sx={{ "&:hover": { bgcolor: "#f8fafc" } }}>
                      <TableCell sx={{ ...tdSx, color: "#94a3b8", width: 36 }}>{i + 1}</TableCell>
                      <TableCell sx={tdSx}><Typography sx={{ fontSize: 11, fontWeight: 700, color: BRAND, fontFamily: "monospace" }}>{row.work_order_no || `#${row.trip_id}`}</Typography></TableCell>
                      <TableCell sx={tdSx}><Typography sx={{ fontSize: 11, color: "#64748b" }}>{row.invoice_no || "—"}</Typography></TableCell>
                      <TableCell sx={tdSx}><Typography sx={{ fontSize: 11, color: "#374151" }} noWrap>{row.client_name || "—"}</Typography></TableCell>
                      <TableCell sx={tdSx}><Typography sx={{ fontSize: 11, color: "#64748b" }}>{row.trip_date ? dayjs(row.trip_date).format("DD MMM YYYY") : "—"}</Typography></TableCell>
                      <TableCell sx={tdSx}><Typography sx={{ fontSize: 12, fontWeight: 700, color: "#2f3891" }}>{fmt(row.revenue)}</Typography></TableCell>
                      <TableCell sx={tdSx}><Typography sx={{ fontSize: 12, color: "#f59e0b", fontWeight: 600 }}>{fmt(row.driver_cost)}</Typography></TableCell>
                      <TableCell sx={tdSx}><Typography sx={{ fontSize: 12, color: "#ef4444", fontWeight: 600 }}>{fmt(row.trip_expenses)}</Typography></TableCell>
                      <TableCell sx={tdSx}>
                        <Box sx={{ display: "inline-block", px: 1, py: 0.25, borderRadius: 1.5, bgcolor: pb }}>
                          <Typography sx={{ fontSize: 12, fontWeight: 800, color: pc }}>{row.profit >= 0 ? "+" : "−"}{fmt(Math.abs(row.profit))}</Typography>
                        </Box>
                      </TableCell>
                      <TableCell sx={tdSx}><Typography sx={{ fontSize: 11, fontWeight: 600, color: row.profit_margin >= 0 ? "#16a34a" : "#dc2626" }}>{row.profit_margin}%</Typography></TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>

        {/* ── Driver Profit Analysis ── */}
        {driverProfitRows.length > 0 && (
          <Card elevation={0} sx={{ border: `1px solid ${BORDER}`, borderRadius: 2.5, overflow: "hidden", mt: 2.5 }}>
            <Box sx={{ px: 2, py: 1.5, display: "flex", alignItems: "center", justifyContent: "space-between", bgcolor: "#fff", borderBottom: `1px solid ${BORDER}` }}>
              <Box>
                <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#0f172a" }}>
                  Driver Profit Analysis
                  <Box component="span" sx={{ ml: 1, fontSize: 11, fontWeight: 500, color: "#64748b" }}>({driverProfitRows.length} drivers)</Box>
                </Typography>
                <Typography sx={{ fontSize: 11, color: "#94a3b8" }}>Revenue, cost & profit grouped by assigned driver</Typography>
              </Box>
            </Box>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    {["#", "Driver Name", "Trips", "Revenue", "Driver Cost", "Expenses", "Profit", "Margin"].map(h => (
                      <TableCell key={h} sx={thSx}>{h}</TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {driverProfitRows.map((row, i) => {
                    const pc = row.profit > 0 ? "#16a34a" : row.profit < 0 ? "#dc2626" : "#f59e0b";
                    const pb = row.profit > 0 ? "rgba(22,163,74,0.07)" : row.profit < 0 ? "rgba(220,38,38,0.07)" : "rgba(245,158,11,0.07)";
                    return (
                      <TableRow key={row.driver} sx={{ "&:hover": { bgcolor: "#f8fafc" } }}>
                        <TableCell sx={{ ...tdSx, color: "#94a3b8", width: 36 }}>{i + 1}</TableCell>
                        <TableCell sx={tdSx}><Typography sx={{ fontSize: 12, fontWeight: 700, color: "#0f172a" }}>{row.driver}</Typography></TableCell>
                        <TableCell sx={tdSx}><Typography sx={{ fontSize: 12, color: "#64748b" }}>{row.trips}</Typography></TableCell>
                        <TableCell sx={tdSx}><Typography sx={{ fontSize: 12, fontWeight: 700, color: BRAND }}>{fmt(row.revenue)}</Typography></TableCell>
                        <TableCell sx={tdSx}><Typography sx={{ fontSize: 12, fontWeight: 600, color: "#f59e0b" }}>{fmt(row.driverCost)}</Typography></TableCell>
                        <TableCell sx={tdSx}><Typography sx={{ fontSize: 12, fontWeight: 600, color: "#ef4444" }}>{fmt(row.expenses)}</Typography></TableCell>
                        <TableCell sx={tdSx}>
                          <Box sx={{ display: "inline-block", px: 1, py: 0.25, borderRadius: 1.5, bgcolor: pb }}>
                            <Typography sx={{ fontSize: 12, fontWeight: 800, color: pc }}>{row.profit >= 0 ? "+" : "−"}{fmt(Math.abs(row.profit))}</Typography>
                          </Box>
                        </TableCell>
                        <TableCell sx={tdSx}><Typography sx={{ fontSize: 11, fontWeight: 600, color: row.margin >= 0 ? "#16a34a" : "#dc2626" }}>{row.margin}%</Typography></TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </Card>
        )}
      </>)}
    </Box>
  );
};

export default BalanceSheet;
